import { appUrl } from '../../config/appUrl';
import { Coffee, UtensilsCrossed, Sparkles, Ticket, LayoutGrid, MapPin, ChevronDown, Search, Map as MapIcon, Heart } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { LandingPlace } from './types';
import { RailSearch } from './RailSearch';
import { formatDistance, track } from './types';

export const heroCategories: ReadonlyArray<{ key: string; label: string; icon: ReactNode }> = [
  { key: 'coffee', label: 'กาแฟ', icon: <Coffee /> },
  { key: 'restaurant', label: 'ร้านอาหาร', icon: <UtensilsCrossed /> },
  { key: 'massage', label: 'บิวตี้ & สุขภาพ', icon: <Sparkles /> },
  { key: 'activity', label: 'กิจกรรม', icon: <Ticket /> },
  { key: '', label: 'ทั้งหมด', icon: <LayoutGrid /> },
];

const rewardBubbles = [
  { emoji: '☕', label: 'รีวิวกาแฟ', reward: 10 },
  { emoji: '🍜', label: 'ลองร้านใหม่', reward: 15 },
  { emoji: '🏯', label: 'รีวิวที่เที่ยว', reward: 5 },
] as const;

type Point = { x: number; y: number };

function noise(key: string, salt: number) {
  let hash = (0x811c9dc5 ^ salt) >>> 0;
  for (let i = 0; i < key.length; i++) hash = Math.imul(hash ^ key.charCodeAt(i), 0x01000193) >>> 0;
  return (hash % 2001) / 1000 - 1;
}

/**
 * Same layout as the app's home map: pins follow the GPS spread, nearby shops
 * attract into clusters, and shops at the same spot fan out instead of hiding.
 */
export function naturalPinLayout(places: Array<Pick<LandingPlace, 'place_id' | 'lat' | 'lng'>>): Point[] {
  const count = places.length;
  if (!count) return [];
  const withCoords = places.filter(place => Number.isFinite(place.lat) && Number.isFinite(place.lng));
  const baseLat = withCoords[0]?.lat ?? 0;
  const cosLat = Math.cos(baseLat * Math.PI / 180);
  const rawX = places.map((place, i) => Number.isFinite(place.lng) ? place.lng! * cosLat : noise(place.place_id, 7) * 1e-3 + i * 1e-9);
  const rawY = places.map((place, i) => Number.isFinite(place.lat) ? -place.lat! : noise(place.place_id, 8) * 1e-3 + i * 1e-9);
  const spread = (values: number[]) => {
    const lo = Math.min(...values);
    const span = Math.max(...values) - lo;
    if (span < 1e-7) return values.map(() => .5);
    const order = values.map((_, i) => i).sort((a, b) => values[a] - values[b]);
    const rank = new Array<number>(values.length).fill(.5);
    if (values.length > 1) order.forEach((index, r) => { rank[index] = r / (order.length - 1); });
    // Mostly true distance, softened by rank so one far shop can't squash the rest.
    return values.map((value, i) => (value - lo) / span * .7 + rank[i] * .3);
  };
  const xs = spread(rawX);
  const ys = spread(rawY);
  const base = places.map((_, i) => ({ x: xs[i], y: ys[i] }));
  const reach = .3;
  const pulled = base.map((point, i) => {
    let sx = 0, sy = 0, weight = 0;
    base.forEach((other, j) => {
      if (i === j) return;
      const distance = Math.hypot(other.x - point.x, other.y - point.y);
      if (distance >= reach) return;
      const w = 1 - distance / reach;
      sx += other.x * w; sy += other.y * w; weight += w;
    });
    if (!weight) return point;
    const t = Math.min(.55, weight * .35);
    return { x: point.x + (sx / weight - point.x) * t, y: point.y + (sy / weight - point.y) * t };
  });
  const result: Point[] = [];
  pulled.forEach((point, i) => {
    const key = places[i].place_id;
    let x = point.x + noise(key, 1) * .04;
    let y = point.y + noise(key, 2) * .04;
    const stacked = result.filter(placed => Math.hypot(placed.x - x, placed.y - y) < .05).length;
    if (stacked) {
      const angle = stacked * 2.39996;
      const radius = .1 * Math.sqrt(stacked);
      x += Math.cos(angle) * radius;
      y += Math.sin(angle) * .7 * radius;
    }
    result.push({ x: Math.min(1, Math.max(0, x)), y: Math.min(1, Math.max(0, y)) });
  });
  return result;
}

function PinPhoto({ src }: { src: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return <span className="hx-pin__photo">{src && !failed ? <img src={src} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" onError={() => setFailed(true)} /> : <Coffee />}</span>;
}

export function HeroMap({ places, city, cityLabel, locale, category, onCategory, onPickCity, onSelectPlace, activePlaceId }: {
  places: LandingPlace[];
  city: string;
  cityLabel: string;
  locale: 'th' | 'en';
  category: string;
  onCategory: (key: string) => void;
  onPickCity: () => void;
  onSelectPlace: (place: LandingPlace) => void;
  activePlaceId?: string;
}) {
  const [searching, setSearching] = useState(false);
  const pins = places.slice(0, 14);
  const layout = naturalPinLayout(pins);
  // Pins live in the open band between the rail and the carousel, and stay
  // clear of the faded left edge that sits behind the hero copy.
  const at = (point: Point) => ({ left: `${14 + point.x * 80}%`, top: `${22 + point.y * 56}%` });
  const centre = layout.length ? layout.reduce((sum, p) => ({ x: sum.x + p.x / layout.length, y: sum.y + p.y / layout.length }), { x: 0, y: 0 }) : { x: .55, y: .5 };
  // Stand the viewer just below the nearest cluster rather than on top of it.
  const you = { x: Math.min(.9, Math.max(.25, centre.x + .04)), y: Math.min(1.12, centre.y + .42) };
  // Label a few well-spaced pins (the active one first) so labels don't pile up.
  const labelled = new Set<string>();
  const labelledAt: Point[] = [];
  [...pins.keys()].sort((a, b) => Number(pins[b].place_id === activePlaceId) - Number(pins[a].place_id === activePlaceId)).forEach(index => {
    const point = layout[index];
    if (labelled.size >= 5 || labelledAt.some(other => Math.abs(other.x - point.x) < .22 && Math.abs(other.y - point.y) < .16)) return;
    labelled.add(pins[index].place_id);
    labelledAt.push(point);
  });
  const paintOrder = pins.map((_, i) => i).sort((a, b) => (pins[a].place_id === activePlaceId ? 1 : 0) - (pins[b].place_id === activePlaceId ? 1 : 0) || layout[a].y - layout[b].y);

  return <div className="hx-map" aria-label={`แผนที่ร้านใกล้คุณใน ${cityLabel}`}>
    <img className="hx-map__bg" src="/assets/beaw-map-background.svg" alt="" aria-hidden="true" draggable="false" />

    {searching ? <div className="hx-rail hx-rail--search"><RailSearch city={city} locale={locale} onClose={() => setSearching(false)} /></div> :
    <div className="hx-rail" role="toolbar" aria-label="เลือกเมืองและหมวดหมู่">
      <button type="button" className="hx-rail__city" onClick={onPickCity}><MapPin /><span>{cityLabel}</span><ChevronDown /></button>
      <div className="hx-rail__pills">
        {heroCategories.map(item => <button type="button" key={item.key || 'all'} className={`hx-pill ${item.key === category ? 'is-active' : ''}`} aria-pressed={item.key === category} onClick={() => onCategory(item.key)}>{item.icon}<span>{item.label}</span></button>)}
      </div>
      <button type="button" className="hx-rail__search" aria-label="ค้นหา" onClick={() => { track('landing_search_click', { city: cityLabel }); setSearching(true); }}><Search /></button>
    </div>}

    <a className="hx-bubbles" href={appUrl('/commu/create')} aria-label="รีวิวกาแฟ +10 บาท ลองร้านใหม่ +15 บาท รีวิวที่เที่ยว +5 บาท — เริ่มเขียนรีวิว" onClick={() => track('landing_reward_bubble_click', { city: cityLabel })}>
      {rewardBubbles.map((bubble, index) => <span key={bubble.label} className={`hx-bubble hx-bubble--${index}`}><span className="hx-bubble__emoji" aria-hidden="true">{bubble.emoji}</span><span><b>{bubble.label}</b><strong>+{bubble.reward} บาท <Heart size={14} fill="currentColor" /></strong></span></span>)}
    </a>

    <div className="hx-you" style={at(you)} aria-hidden="true">
      <span className="hx-you__card"><MapPin /><span><b>คุณอยู่ที่นี่</b><small>{cityLabel}</small></span></span>
      <span className="hx-you__dot" />
    </div>

    {paintOrder.map(index => {
      const place = pins[index];
      const active = place.place_id === activePlaceId;
      return <button type="button" key={place.place_id} className={`hx-pin ${active ? 'is-active' : ''} ${place.has_campaign ? 'has-reward' : ''}`} style={{ ...at(layout[index]), '--pin-order': index } as CSSProperties} aria-label={place.name} aria-pressed={active} onClick={() => onSelectPlace(place)}>
        <span className="hx-pin__head"><PinPhoto src={place.image_url} /></span>
        {labelled.has(place.place_id) && <span className="hx-pin__label"><b>{place.name}</b><small>{formatDistance(place.distance_m)}</small></span>}
      </button>;
    })}

    <a className="hx-fullmap" href={appUrl('/campaigns')} onClick={() => track('landing_full_map_click', { city: cityLabel })}><MapIcon />ดูแผนที่แบบเต็มจอ</a>
  </div>;
}
